import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PublicRestaurant, { PublicRestaurantProvider } from './components/restaurant/PublicRestaurant';
import CustomerOrderingGate from './components/restaurant/CustomerOrderingGate';
import { useAuth } from './hooks/auth/useAuth';
import { useBrandingInit } from './hooks/branding/useBrandingInit';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './routes/ProtectedRoute';
import GlobalAuthIssueBanner from './components/common/GlobalAuthIssueBanner';
import { useStaffSessionGuard } from './hooks/auth/useStaffSessionGuard';
import AssistantErrorBoundary from './components/assistant/AssistantErrorBoundary';
import { staffHomeRoute, staffRoleCanUseAssistant } from './services/staff/staffRole.workflow';
import { systemCopilotCanOpen } from './services/assistant/systemCopilotNavigation.workflow';
import AdminThemeController from './components/admin/AdminThemeController';

const Layout = lazy(() => import('./components/Layout'));
const RestaurantHome = lazy(() => import('./pages/restaurant/RestaurantHome'));
const WebsiteOrderPractice = lazy(() => import('./pages/restaurant/WebsiteOrderPractice'));
const TouchInputKeyboard = lazy(() => import('./components/common/TouchInputKeyboard'));
const RestaurantAssistantDrawer = lazy(() => import('./components/assistant/RestaurantAssistantDrawer'));
const SystemCopilotDock = lazy(() => import('./components/assistant/SystemCopilotDock'));
const GuidedTraining = lazy(() => import('./components/training/GuidedTraining'));
const Login = lazy(() => import('./pages/Login'));
const CustomerDashboard = lazy(() => import('./pages/customer/Dashboard'));
const MenuPage = lazy(() => import('./pages/customer/MenuPage'));
const OrderHistory = lazy(() => import('./pages/customer/OrderHistory'));
const ActiveOrders = lazy(() => import('./pages/customer/ActiveOrders'));
const Checkout = lazy(() => import('./pages/customer/Checkout'));
const DeliveryCheckout = lazy(() => import('./pages/customer/DeliveryCheckout'));
const PickupCheckout = lazy(() => import('./pages/customer/PickupCheckout'));
const PaymentPage = lazy(() => import('./pages/customer/PaymentPage'));
const OrderConfirmation = lazy(() => import('./pages/customer/OrderConfirmation'));
const OrderStatusPage = lazy(() => import('./pages/customer/OrderStatus/OrderStatusPage'));
const ChatPage = lazy(() => import('./pages/chat/ChatPage'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const POSWorkspacePage = lazy(() => import('./pages/admin/pos/POSWorkspacePage'));
const KitchenDisplayManagement = lazy(() => import('./pages/admin/KitchenDisplayManagement'));
const DriverDashboard = lazy(() => import('./pages/driver/Dashboard'));
const CallCenterDashboard = lazy(() => import('./pages/callCenter/Dashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const Support = lazy(() => import('./pages/Support'));
const PhoneOrderReview = lazy(() => import('./pages/PhoneOrderReview'));
const DeliveryLocationRequest = lazy(() => import('./pages/DeliveryLocationRequest'));
const GovernedRoleWorkspace = lazy(() => import('./pages/staff/GovernedRoleWorkspace'));

function LoginRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const from = location.state?.from;
  const returnTo = typeof from === 'string' && /^\/(checkout(?:\/|$)|order-history$|active-orders$|account$)/.test(from) && !from.includes('//') ? from : '/account';
  if (loading) return <LoadingSpinner />;
  return user ? <Navigate to={user.role === 'customer' ? returnTo : staffHomeRoute(user.role)} replace /> : <Login />;
}

const App: React.FC = () => {
  const { user } = useAuth();
  useStaffSessionGuard(user);
  
  // Initialize branding (logo)
  useBrandingInit();

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PublicRestaurantProvider>
      <AdminThemeController />
      <GlobalAuthIssueBanner />
      <Suspense fallback={null}>
        {(user?.role === 'admin' || user?.role === 'call_center') && <TouchInputKeyboard />}
        {user && staffRoleCanUseAssistant(user.role) && (
          <AssistantErrorBoundary>
            <RestaurantAssistantDrawer user={user} />
          </AssistantErrorBoundary>
        )}
        {user && systemCopilotCanOpen(user.role) && (
          <AssistantErrorBoundary>
            <SystemCopilotDock key={`${user.id}:${user.role}`} user={user} />
          </AssistantErrorBoundary>
        )}
        {user && <GuidedTraining user={user} />}
      </Suspense>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
        <Routes>
        <Route 
          path="/login" 
          element={<LoginRoute />}
        />
        
        {/* Public routes */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/support" element={<Support />} />
        <Route path="/phone-order/:draftId" element={<PhoneOrderReview />} />
        <Route path="/delivery-location/:orderId" element={<DeliveryLocationRequest />} />
        <Route path="/practice/:runId" element={<ProtectedRoute allowedRoles={['admin']}><WebsiteOrderPractice /></ProtectedRoute>} />
        <Route element={<PublicRestaurant />}>
          <Route path="/" element={<RestaurantHome />} />
          <Route path="/menu" element={<MenuPage />} />
        </Route>

        <Route
          path="/pos"
          element={
            <ProtectedRoute allowedRoles={['admin', 'cashier']}>
              <POSWorkspacePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kitchen"
          element={
            <ProtectedRoute allowedRoles={['admin', 'kitchen']}>
              <KitchenDisplayManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/call-center"
          element={
            <ProtectedRoute allowedRoles={['admin', 'call_center']}>
              <CallCenterDashboard />
            </ProtectedRoute>
          }
        />
        
        <Route path="/" element={<Layout />}>
          <Route
            path="/account"
            element={
              <ProtectedRoute requiredRole="customer">
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-history"
            element={
              <ProtectedRoute requiredRole="customer">
                <OrderHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/active-orders"
            element={
              <ProtectedRoute requiredRole="customer">
                <ActiveOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout"
            element={
              <ProtectedRoute requiredRole="customer">
                <CustomerOrderingGate><Checkout /></CustomerOrderingGate>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/delivery"
            element={
              <ProtectedRoute requiredRole="customer">
                <CustomerOrderingGate><DeliveryCheckout /></CustomerOrderingGate>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/pickup"
            element={
              <ProtectedRoute requiredRole="customer">
                <CustomerOrderingGate><PickupCheckout /></CustomerOrderingGate>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/payment"
            element={
              <ProtectedRoute requiredRole="customer">
                <PaymentPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-confirmation"
            element={
              <ProtectedRoute requiredRole="customer">
                <OrderConfirmation />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-status/:orderId"
            element={
              <ProtectedRoute requiredRole="customer">
                <OrderStatusPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/chat/:recipientId"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/staff-workspace"
            element={
              <ProtectedRoute allowedRoles={['manager', 'accountant', 'cashier', 'kitchen']}>
                <GovernedRoleWorkspace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/driver"
            element={
              <ProtectedRoute requiredRole="driver">
                <DriverDashboard />
              </ProtectedRoute>
            }
          />

        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </PublicRestaurantProvider>
    </BrowserRouter>
  );
};

export default App;
